import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import type { CostSettingProfile } from 'lumen/models';
import ApplicationSerializer from 'lumen/serializers/application';

/**
 * A save response returns the cost with a server id, which EmberData loads as
 * a second record. Dropping the client-created one keeps the list at one pill.
 */
function unloadClientCreatedCosts(profile: CostSettingProfile | null): void {
  profile?.costs
    .filter((cost) => cost.isNew)
    .forEach((cost) => cost.unloadRecord());
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class CostSettingProfileSerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  attrs = {
    costs: { embedded: 'always' },
    createdAt: { serialize: false },
    updatedAt: { serialize: false },
  };

  modelNameFromPayloadKey(key: string): string {
    if (key === 'profile' || key === 'profiles') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      return super.modelNameFromPayloadKey('cost-setting/profile');
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return super.modelNameFromPayloadKey(key);
  }

  normalizeCreateRecordResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: Record<string, unknown>,
    id: string | number,
    requestType: string,
  ): Record<string, unknown> {
    // No id until this response is normalized, so find it by `isNew`.
    unloadClientCreatedCosts(
      store
        .peekAll<CostSettingProfile>('cost-setting/profile')
        .find((candidate) => candidate.isNew) ?? null,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return super.normalizeCreateRecordResponse(
      store,
      primaryModelClass,
      payload,
      id,
      requestType,
    );
  }

  normalizeUpdateRecordResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: Record<string, unknown>,
    id: string | number,
    requestType: string,
  ): Record<string, unknown> {
    unloadClientCreatedCosts(
      store.peekRecord<CostSettingProfile>('cost-setting/profile', id),
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return super.normalizeUpdateRecordResponse(
      store,
      primaryModelClass,
      payload,
      id,
      requestType,
    );
  }

  payloadKeyFromModelName(): string {
    return 'profile';
  }
}
